/**
 * DPR frontend entry.
 * - home: Lenis, header, drawer, reveal, intro, YouTube, hero, artist, shop, auth UI, comm UI.
 * - admin: password toggle, admin forms.
 * - 라우트 연동: data-controller / data-action 에 따라 해당 TS 컨트롤러 메소드만 추가 실행.
 */
import { DomHelpers } from "@/utils/DomHelpers";
import { Logger } from "./utils/Logger";
import { Toast, showFlashToasts } from "./utils/Toast";
import { Modal } from "./utils/Modal";

// Home – global, controllers, components
import { LenisController, initBackToTop } from "./home/global/LenisController";
import { initReveal } from "./home/global/RevealController";
import { initSubGalleryFade } from "./home/global/SubGalleryFadeController";
import { initHeaderEffects } from "./home/global/HeaderEffects";
import { initHeaderBgm } from "./home/global/HeaderBgmController";
import { initMainPopup } from "./home/global/MainPopupController";
import { initDrawer } from "./home/global/DrawerController";
import { IntroController } from "./home/controllers/IntroController";
import { HomeController } from "./home/controllers/HomeController";
import { AuthController } from "./home/controllers/AuthController";
import { HeroSlider } from "./home/components/HeroSlider";
import { HeroCursor } from "./home/components/HeroCursor";
import { YoutubeClipManager } from "./home/components/YoutubeClipManager";
import { ArtistVideoController } from "./home/components/ArtistVideoController";
import { initVideoModal } from "./home/components/VideoModalController";
import { ArtistCursor } from "./home/components/ArtistCursor";
import { ShopSlider } from "./home/components/ShopSlider";
import { ArtSlider } from "./home/components/ArtSlider";
import { PasswordToggleInput } from "./home/components/PasswordToggleInput";
import { CountrySelect } from "./home/components/CountrySelect";
import { SidebarDropdown } from "./home/components/SidebarDropdown";
import { PinPostSwiper } from "./home/components/PinPostSwiper";
import { initPostMoreMenus } from "./home/components/PostMoreMenu";
import { CommSortSelect } from "./home/components/CommSortSelect";
import { CommController } from "./home/controllers/CommController";
import { initHomeModule } from "./home/HomeModule";
import { initMusicVideoList } from "./home/work/MusicVideoList";
import { initWorkShowcaseList } from "./home/work/WorkShowcaseList";
import { initDirectorsPage } from "./home/work/DirectorsPage";
import { DirectorHeroSwiper } from "./home/components/DirectorHeroSwiper";

// Admin – global, controllers (admin.php 라우트와 동기화)
import {
    AdminAuthController,
    AdminNoticeController,
    AdminController,
    BoardController,
    TagController,
    PostApprovalController,
    RoleController,
    InvitationController,
    UserController,
    AdminArtistController,
    AdminContactController,
    AdminMainVideoController,
    AdminMusicController,
    AdminShopProductController,
    AdminMusicVisualController,
    AdminPlaylistController,
    AdminSiteinfoController,
    AdminCommercialVideoController,
    AdminMusicVideoController,
    AdminPromotionVideoController,
    AdminDirectorController,
} from "./admin/controllers";
import {
    initAdminForms,
    initAdminDateTimeInputs,
    initAdminSelects,
    initFileUploaders,
    initAdminLayoutSidebar,
    initAdminNotificationsDropdown,
} from "./admin/global";

type ControllerAction = () => void;
type ControllerMap = Record<string, ControllerAction>;

/** PHP current_controller() / current_action() 와 이름 일치 (라우트 기반 자동 디스패치) */
const CONTROLLER_REGISTRY: Record<string, ControllerMap> = {
    AdminAuthController,
    AdminNoticeController,
    AdminController,
    BoardController,
    TagController,
    PostApprovalController,
    RoleController,
    InvitationController,
    UserController,
    AdminArtistController,
    AdminContactController,
    AdminMainVideoController,
    AdminMusicController,
    AdminShopProductController,
    AdminMusicVisualController,
    AdminPlaylistController,
    AdminSiteinfoController,
    AdminCommercialVideoController,
    AdminMusicVideoController,
    AdminPromotionVideoController,
    AdminDirectorController,
    AuthController,
    CommController,
    HomeController,
};

function getScriptAttrs(): { controller: string; action: string } {
    const script = document.querySelector<HTMLScriptElement>(
        'script[src*="app.js"]',
    );
    if (!script) return { controller: "", action: "" };
    return {
        controller: (script.getAttribute("data-controller") ?? "").trim(),
        action: (script.getAttribute("data-action") ?? "").trim(),
    };
}

function dispatchController(controller: string, action: string): void {
    if (!controller || !action) return;
    const ctrl = CONTROLLER_REGISTRY[controller];
    if (!ctrl) {
        Logger.debug("app.dispatch: unknown controller", controller);
        return;
    }
    const fn = (ctrl as ControllerMap)[action];
    if (typeof fn !== "function") {
        Logger.debug("app.dispatch: unknown action", controller, action);
        return;
    }
    try {
        fn.call(ctrl);
    } catch (e) {
        Logger.error("app.dispatch failed", controller, action, e);
    }
}

function initPagination(): void {
    document.querySelectorAll(".no-pagination a[data-page]").forEach((link) => {
        link.addEventListener("click", (e) => {
            const page = (e.currentTarget as HTMLElement).getAttribute(
                "data-page",
            );
            if (
                !page ||
                (e.currentTarget as HTMLAnchorElement).hasAttribute("disabled")
            ) {
                e.preventDefault();
                return;
            }
            e.preventDefault();
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.set("page", page);
            window.location.href =
                window.location.pathname + "?" + urlParams.toString();
        });
    });
}

function runGlobalInits(): void {
    initPagination();

    const isAdmin = document.body.classList.contains("admin");

    if (isAdmin) {
        showFlashToasts();
        new PasswordToggleInput(document).init();
        initAdminForms();
        initAdminDateTimeInputs();
        initAdminSelects();
        initFileUploaders();
        initAdminLayoutSidebar();
        initAdminNotificationsDropdown();
        return;
    }

    new LenisController().init();
    initBackToTop();
    initHomeModule();
    initHeaderEffects();
    initHeaderBgm();
    if (document.querySelector(".main-popup-wrap")) initMainPopup();
    initDrawer();
    initReveal();
    initSubGalleryFade();
    new IntroController().init();

    YoutubeClipManager.onReady(() => YoutubeClipManager.initAll());

    initVideoModal();

    // 메인 비주얼(.no-visual)이 있는 페이지(index)는 main.js가 슬라이드+플레이어를 담당하므로 Swiper 중복 초기화 방지
    const hasMainVisual = document.querySelector(".no-visual");
    if (!hasMainVisual) {
        const heroSlider = new HeroSlider();
        heroSlider.init();
    }
    const heroEl = document.querySelector<HTMLElement>(".no-main-hero__slider");
    if (heroEl) {
        try {
            new HeroCursor(heroEl).init();
        } catch {
            /* no .cursor-title */
        }
    }

    const artistSection = DomHelpers.qs<HTMLElement>(document, ".no-artist");
    if (artistSection) {
        try {
            new ArtistVideoController(artistSection).init();
        } catch {
            /* missing elements */
        }
        try {
            new ArtistCursor(artistSection).init();
        } catch {
            /* no .cursor-title */
        }
    }

    new ShopSlider().init();

    if (document.querySelector(".sub-artist-view .art-slider")) {
        new ArtSlider().init();
    }

    new PasswordToggleInput(
        document.querySelector(".auth-page") ??
            document.querySelector(".profile-page") ??
            document,
    ).init();
    new CountrySelect("#auth-country").init();

    const commSidebar = document.querySelector<HTMLElement>(".comm-sidebar");
    if (commSidebar) {
        new SidebarDropdown(commSidebar).init();
    }

    if (document.querySelector(".exclusive-pinned-swiper")) {
        new PinPostSwiper().init();
    }

    if (document.querySelector("[data-post-more]")) {
        initPostMoreMenus();
    }

    if (document.querySelector(".comm-page__sort")) {
        new CommSortSelect().init();
    }

    if (document.querySelector("[data-mv-list]")) {
        initMusicVideoList();
    }

    if (document.querySelector("[data-ws-list]")) {
        initWorkShowcaseList();
    }

    if (document.querySelector("[data-dir-page]")) {
        initDirectorsPage();
    }

    if (document.querySelector("[data-dir-hero-swiper]")) {
        new DirectorHeroSwiper().init();
    }

    const boardListPage = document.querySelector(
        ".comm-page[data-api-board-list]",
    );
    const boardPostsContainer =
        boardListPage?.querySelector<HTMLElement>("[data-posts-list]");
    if (boardListPage && boardPostsContainer) {
        const apiUrl = boardListPage.getAttribute("data-api-board-list");
        const boardCode = boardListPage.getAttribute("data-board-code");
        if (apiUrl && boardCode) {
            const useGallery =
                boardListPage.getAttribute("data-list-style") === "gallery";
            const list = new PostList({
                container: boardPostsContainer,
                fetchUrl: apiUrl,
                ItemClass: useGallery ? PostGalleryItem : undefined,
                getParams: () => ({
                    code: boardCode,
                    tag:
                        new URLSearchParams(window.location.search).get(
                            "tag",
                        ) ?? "",
                }),
                emptyMessage:
                    boardListPage.getAttribute("data-empty-message") ??
                    "No posts yet.",
                loadErrorMessage:
                    boardListPage.getAttribute("data-load-error-message") ??
                    undefined,
                boardCode: boardCode ?? undefined,
                onRetry: () => list.load(),
                defaultImageUrl:
                    boardListPage.getAttribute("data-default-thumb") ??
                    undefined,
            });
            list.load();
        }
    }
}

function init(): void {
    (window as unknown as { Toast: typeof Toast; Modal: typeof Modal }).Toast =
        Toast;
    (window as unknown as { Toast: typeof Toast; Modal: typeof Modal }).Modal =
        Modal;
    runGlobalInits();

    const { controller, action } = getScriptAttrs();
    if (controller && action) {
        dispatchController(controller, action);
    }
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
